import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Crown, Zap, ArrowRight, Sparkles, Gift } from 'lucide-react';

// Track user workflow progress
const WORKFLOW_KEY = 'mockuppro_workflow_progress';

// Get workflow progress from localStorage
const getWorkflowProgress = () => {
  try {
    const stored = localStorage.getItem(WORKFLOW_KEY);
    return stored ? JSON.parse(stored) : { generate: 0, makeItBetter: 0, upscale: 0, lastReset: Date.now() };
  } catch {
    return { generate: 0, makeItBetter: 0, upscale: 0, lastReset: Date.now() };
  }
};

// Update workflow progress
const updateWorkflowProgress = (action) => {
  const progress = getWorkflowProgress();
  
  // Reset progress if it's been more than 1 hour (fresh session)
  if (Date.now() - progress.lastReset > 3600000) {
    progress.generate = 0;
    progress.makeItBetter = 0;
    progress.upscale = 0;
    progress.lastReset = Date.now();
  }
  
  progress[action] = (progress[action] || 0) + 1;
  localStorage.setItem(WORKFLOW_KEY, JSON.stringify(progress));
  return progress;
};

// Check if user completed the power workflow (Generate → Make It Better → Upscale)
const checkPowerWorkflow = () => {
  const progress = getWorkflowProgress();
  return progress.generate >= 1 && progress.makeItBetter >= 1 && progress.upscale >= 1;
};

// Reset workflow progress (after showing upgrade modal)
const resetWorkflowProgress = () => {
  localStorage.setItem(WORKFLOW_KEY, JSON.stringify({ 
    generate: 0, 
    makeItBetter: 0, 
    upscale: 0, 
    lastReset: Date.now() 
  }));
};

// Hook to track workflow actions and trigger upgrade modals
export const useUpgradeTrigger = () => {
  const { user } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [triggerReason, setTriggerReason] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  
  const credits = user?.credits || 0;
  const plan = user?.plan || 'free';
  const isAdmin = user?.is_admin;
  const isPaidUser = plan !== 'free';
  
  // Track an action and check if we should show upgrade modal
  const trackAction = useCallback((action) => {
    // Don't show for admin or paid users
    if (isAdmin || isPaidUser) return;
    
    const progress = updateWorkflowProgress(action);
    
    // Check if user completed the power workflow
    if (checkPowerWorkflow() && !dismissed) {
      setTriggerReason('power_workflow');
      setShowUpgradeModal(true);
      resetWorkflowProgress(); // Reset so it doesn't trigger again immediately
    }
  }, [isAdmin, isPaidUser, dismissed]);
  
  // Check for low credits trigger
  const checkLowCredits = useCallback(() => {
    if (isAdmin || isPaidUser || dismissed) return;
    
    if (credits <= 5 && credits > 0) {
      setTriggerReason('low_credits');
      setShowUpgradeModal(true);
    }
  }, [credits, isAdmin, isPaidUser, dismissed]);
  
  // Trigger instant upgrade modal for credit failure
  const triggerCreditFailure = useCallback(() => {
    if (isAdmin || isPaidUser) return;
    
    setTriggerReason('credit_failure');
    setShowUpgradeModal(true);
  }, [isAdmin, isPaidUser]);
  
  // Trigger upgrade modal for plan entitlement errors (model/feature requires upgrade)
  const triggerPlanEntitlement = useCallback(() => {
    if (isAdmin || isPaidUser) return;
    
    setTriggerReason('plan_entitlement');
    setShowUpgradeModal(true);
  }, [isAdmin, isPaidUser]);
  
  // Dismiss and don't show again for this session
  const dismissModal = useCallback(() => {
    setShowUpgradeModal(false);
    setDismissed(true);
    // Store dismissal timestamp
    sessionStorage.setItem('upgrade_dismissed', Date.now().toString());
  }, []);
  
  // Check if modal was dismissed recently (within 30 minutes)
  useEffect(() => {
    const dismissedAt = sessionStorage.getItem('upgrade_dismissed');
    if (dismissedAt) {
      const timeSinceDismiss = Date.now() - parseInt(dismissedAt);
      if (timeSinceDismiss < 1800000) { // 30 minutes
        setDismissed(true);
      }
    }
  }, []);
  
  return {
    showUpgradeModal,
    triggerReason,
    trackAction,
    checkLowCredits,
    triggerCreditFailure,
    triggerPlanEntitlement,
    dismissModal,
    setShowUpgradeModal
  };
};

// Upgrade Modal Component
export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  reason = 'low_credits',
  onNavigate 
}) => {
  const navigate = useNavigate();
  
  const handleUpgrade = () => {
    onClose();
    if (onNavigate) {
      onNavigate();
    } else {
      navigate('/dashboard/settings?tab=billing');
    }
  };
  
  const handleBuyCredits = () => {
    onClose();
    navigate('/dashboard/settings?tab=billing&action=credits');
  };
  
  // Different content based on trigger reason
  const getModalContent = () => {
    switch (reason) {
      case 'power_workflow':
        return {
          title: "You're on Fire!",
          description: "You've unlocked the full creative workflow. Upgrade to Pro for unlimited potential.",
          icon: <Zap className="w-8 h-8 text-amber-500" />,
          highlight: "50% more credits per month",
          cta: "Upgrade to Pro"
        };
      case 'credit_failure':
        return {
          title: "Out of Credits",
          description: "Your creative flow doesn't have to stop. Get more credits now and keep creating.",
          icon: <Gift className="w-8 h-8 text-red-500" />,
          highlight: "Instant credit delivery",
          cta: "Get Credits Now"
        };
      case 'plan_entitlement':
        return {
          title: "Feature Requires Upgrade",
          description: "This feature is available on a higher plan. Unlock advanced models and premium features.",
          icon: <Crown className="w-8 h-8 text-amber-500" />,
          highlight: "Access all premium models",
          cta: "Upgrade Your Plan"
        };
      case 'low_credits':
      default:
        return {
          title: "Running Low on Credits",
          description: "You're almost out! Don't let your momentum stop - get more credits or upgrade for better value.",
          icon: <Sparkles className="w-8 h-8 text-violet-500" />,
          highlight: "Pro users save 40%",
          cta: "Upgrade Now"
        };
    }
  };
  
  const content = getModalContent();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center">
            {content.icon}
          </div>
          <DialogTitle className="text-xl">{content.title}</DialogTitle>
          <DialogDescription className="text-base mt-2">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        
        {/* Highlight Badge */}
        <div className="bg-gradient-to-r from-violet-500/10 to-purple-600/10 border border-violet-500/20 rounded-lg p-3 text-center">
          <span className="text-sm font-medium text-violet-600 dark:text-violet-400">
            {content.highlight}
          </span>
        </div>
        
        {/* Plan Benefits */}
        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Crown className="w-4 h-4 text-amber-500" />
            <span>Higher quality generations</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Zap className="w-4 h-4 text-violet-500" />
            <span>Priority processing</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-blue-500" />
            <span>All AI models unlocked</span>
          </div>
        </div>
        
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button 
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
            data-testid="upgrade-cta-btn"
          >
            {content.cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          {reason !== 'credit_failure' && (
            <Button 
              variant="outline" 
              onClick={handleBuyCredits}
              className="w-full"
              data-testid="buy-credits-btn"
            >
              Just Buy Credits
            </Button>
          )}
          
          <button 
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
          >
            Maybe later
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Export helper to track actions from anywhere
export const trackWorkflowAction = updateWorkflowProgress;

export default UpgradeModal;
